/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { type PropsWithChildren, useMemo } from 'react';

import { cssMap, jsx } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled';
import UFOSegment from '@atlaskit/react-ufo/segment';

import { type PrimaryProductKey } from '../../constants/products';
import { serveJiraNinFpsNavigation } from '../../utils/experiments/experiment-exports';
import { useAppContext } from '../app-context';
import { WidthObserverElement } from '../width-observer';

const styles = cssMap({
	layout: {
		display: 'flex',
		flexDirection: 'column',
		overflow: 'hidden',
	},
});

const NAV3_HEIGHT = 56;
const NAV4_HEIGHT = 48;
// 48px (tabs) + 1px (border)
const FPS_NIN_TABS_HEIGHT = 49;
const FPS_NIN_BUTTON_ICONS_HEIGHT = 56;

export const Layout = ({
	children,
	primaryProduct,
}: PropsWithChildren<{ primaryProduct?: PrimaryProductKey }>) => {
	const { isNav4Enabled } = useAppContext();

	const rovoNavigationHeight = useMemo(() => {
		const cohort = serveJiraNinFpsNavigation({
			primaryProduct,
			fireLayerExposure: false,
		});

		if (cohort === 'tabs') {
			return FPS_NIN_TABS_HEIGHT;
		}

		if (cohort === 'button' || cohort === 'icons') {
			return FPS_NIN_BUTTON_ICONS_HEIGHT;
		}

		return 0;
	}, [primaryProduct]);

	const navHeight = (isNav4Enabled ? NAV4_HEIGHT : NAV3_HEIGHT) + rovoNavigationHeight;
	return (
		<UFOSegment name="search-page-layout">
			<WidthObserverElement id="layout">
				<Box
					testId="search-page-layout"
					xcss={styles.layout}
					// Can be moved to CSS when nav4 is GA
					style={{ height: `calc(100vh - ${navHeight}px)` }}
				>
					{children}
				</Box>
			</WidthObserverElement>
		</UFOSegment>
	);
};
